import { useAccessRequestNodeFragment } from '../gql/fragments/generated/AccessRequestNode';
import type { GrantedProject } from '../types';

/**
 * Retrieves the parent project information for an access request.
 *
 * Since access requests are approved at the project level, this hook determines and returns
 * the correct parent project regardless of whether the access request originated from a
 * project, asset, folder, or collection.
 *
 * @param {string} params.id - The ID of the access request.
 *
 * @returns {GrantedProject | undefined} The parent project with id and name, or undefined if
 * the requested resource or its parent project cannot be found.
 *
 * @example
 * // Get project for an access request
 * const project = useGrantedProject({ id: 'access-request-123' });
 * // project = { id: 'project-456', name: 'My Project' }
 */
export const useGrantedProject = ({
  id,
}: {
  id: string;
}): GrantedProject | undefined => {
  const { data } = useAccessRequestNodeFragment({
    from: {
      id,
      __typename: 'AccessRequest',
    },
  });
  const { requestedResource } = data;

  if (!requestedResource) return undefined;

  switch (requestedResource.__typename) {
    case 'Project':
      return {
        id: requestedResource.id ?? '',
        name: requestedResource.name ?? '',
      };
    case 'FolderAsset':
      return requestedResource.folderAssetProject
        ? {
            id: requestedResource.folderAssetProject.id ?? '',
            name: requestedResource.folderAssetProject.name ?? '',
          }
        : undefined;
    case 'Collection':
      return requestedResource.collectionProject
        ? {
            id: requestedResource.collectionProject.id ?? '',
            name: requestedResource.collectionProject.name ?? '',
          }
        : undefined;
    default:
      /** All Asset types (FileAsset, ImageAsset, VideoAsset, etc.) use the 'project' field */
      if ('project' in requestedResource && requestedResource.project) {
        return {
          id: requestedResource.project.id ?? '',
          name: requestedResource.project.name ?? '',
        };
      }
      return undefined;
  }
};
